"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  Calendar,
  CheckCircle2,
  Clock3,
  FileText,
  FolderKanban,
  Pin,
  Sparkles,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { ListView } from "@/components/issues/ListView";
import { Avatar, AvatarStack } from "@/components/ui/Avatar";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import {
  deriveTeamInsights,
  type TeamAttentionItem,
} from "@/lib/teamInsights";
import { useStore } from "@/lib/store";
import type { AgentRun, Doc, Issue, Project, User } from "@/lib/types";
import { cn, formatDate, formatRelative } from "@/lib/utils";
import { humanOwnerId } from "@/lib/taskOwnership";
import { docIdentifier } from "@/lib/docIdentity";

type TeamTab = "overview" | "tasks" | "context" | "activity";

export interface TeamCommandCenterProps {
  teamId: string;
  canWrite: boolean;
  onOpenTask: (taskId: string) => void;
  onOpenProject: (projectId: string) => void;
  onOpenDoc: (docId: string) => void;
  onOpenAsk: () => void;
  onNavigateTasks: () => void;
}

const TABS: Array<{ id: TeamTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "tasks", label: "Tasks" },
  { id: "context", label: "Context" },
  { id: "activity", label: "Activity" },
];

export function TeamCommandCenter({
  teamId,
  canWrite,
  onOpenTask,
  onOpenProject,
  onOpenDoc,
  onOpenAsk,
  onNavigateTasks,
}: TeamCommandCenterProps) {
  const activeWorkspaceId = useStore((state) => state.activeWorkspaceId);
  const team = useStore((state) =>
    state.teams.find(
      (candidate) =>
        candidate.id === teamId &&
        candidate.workspaceId === activeWorkspaceId
    )
  );
  const issues = useStore((state) => state.issues);
  const projects = useStore((state) => state.projects);
  const docs = useStore((state) => state.docs);
  const agentRuns = useStore((state) => state.agentRuns);
  const users = useStore((state) => state.users);
  const [tab, setTab] = useState<TeamTab>("overview");

  const insight = useMemo(
    () =>
      team
        ? deriveTeamInsights({ team, issues, projects, docs, agentRuns })
        : null,
    [agentRuns, docs, issues, projects, team]
  );
  const members = useMemo(
    () =>
      team
        ? team.memberIds
            .map((id) => users.find((user) => user.id === id))
            .filter((user): user is User => user != null)
        : [],
    [team, users]
  );

  if (!team || !insight) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center bg-[var(--bg)] px-6 text-center">
        <div>
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--fg-muted)]">
            <FolderKanban size={18} />
          </div>
          <h2 className="mt-3 text-[14px] font-medium text-[var(--fg)]">
            Team not found
          </h2>
          <p className="mt-1 text-[11.5px] text-[var(--fg-tertiary)]">
            This team may have been removed or belongs to another workspace.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[var(--bg)]">
      <header className="shrink-0 border-b border-[var(--border)] bg-[var(--bg-secondary)]">
        <div className="mx-auto w-full max-w-[1240px] px-4 pt-4 md:px-6">
          <div className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-start">
            <div
              aria-hidden="true"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border text-[19px]"
              style={{
                color: team.color,
                borderColor: `color-mix(in srgb, ${team.color} 38%, var(--border))`,
                background: `color-mix(in srgb, ${team.color} 10%, var(--bg-secondary))`,
              }}
            >
              {team.icon}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h1 className="truncate text-[20px] font-medium tracking-tight text-[var(--fg)]">
                  {team.name}
                </h1>
                <span className="rounded border border-[var(--border)] bg-[var(--bg-tertiary)] px-1.5 py-0.5 font-mono text-[9.5px] text-[var(--fg-muted)]">
                  {team.key}
                </span>
              </div>
              <p className="mt-0.5 max-w-[720px] text-[12px] leading-5 text-[var(--fg-tertiary)]">
                {team.description ||
                  "The work, context, and agent activity that matters to this team."}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <AvatarStack users={members} size={20} max={4} />
                <span className="text-[10.5px] text-[var(--fg-muted)]">
                  {members.length} {members.length === 1 ? "member" : "members"}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={onOpenAsk}
              title="Ask Pauta about this team"
              className="focus-ring inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md bg-[var(--accent)] px-3 text-[12px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)]"
            >
              <Sparkles size={12} />
              Ask this team
            </button>
          </div>

          <nav
            className="-mb-px flex min-w-0 gap-1 overflow-x-auto scrollbar-thin"
            aria-label={`${team.name} sections`}
            role="tablist"
          >
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                id={`team-${team.id}-tab-${item.id}`}
                aria-controls={`team-${team.id}-panel-${item.id}`}
                aria-selected={tab === item.id}
                tabIndex={tab === item.id ? 0 : -1}
                onClick={() => setTab(item.id)}
                onKeyDown={(event) => {
                  if (
                    event.key !== "ArrowLeft" &&
                    event.key !== "ArrowRight" &&
                    event.key !== "Home" &&
                    event.key !== "End"
                  ) {
                    return;
                  }
                  event.preventDefault();
                  const currentIndex = TABS.findIndex(
                    (candidate) => candidate.id === item.id
                  );
                  const next =
                    event.key === "Home"
                      ? TABS[0]
                      : event.key === "End"
                        ? TABS[TABS.length - 1]
                        : TABS[
                            (currentIndex +
                              (event.key === "ArrowRight" ? 1 : -1) +
                              TABS.length) %
                              TABS.length
                          ];
                  setTab(next.id);
                  requestAnimationFrame(() =>
                    document
                      .getElementById(`team-${team.id}-tab-${next.id}`)
                      ?.focus()
                  );
                }}
                className={cn(
                  "focus-ring relative shrink-0 rounded-t-md px-3 py-2 text-[11.5px] font-medium transition-colors",
                  tab === item.id
                    ? "text-[var(--fg)]"
                    : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg-secondary)]"
                )}
              >
                {item.label}
                {tab === item.id && (
                  <span className="absolute inset-x-2 bottom-0 h-px bg-[var(--accent)]" />
                )}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.15 }}
          className="focus-ring flex min-h-0 flex-1 flex-col"
          role="tabpanel"
          tabIndex={0}
          id={`team-${team.id}-panel-${tab}`}
          aria-labelledby={`team-${team.id}-tab-${tab}`}
        >
          {tab === "overview" && (
            <OverviewTab
              attention={insight.attention}
              activeProjects={insight.activeProjects}
              contextDocs={insight.contextDocs}
              agentRuns={insight.currentAgentRuns}
              issues={insight.teamIssues}
              users={users}
              onOpenTask={onOpenTask}
              onOpenProject={onOpenProject}
              onOpenDoc={onOpenDoc}
              onNavigateTasks={onNavigateTasks}
            />
          )}
          {tab === "tasks" && (
            <ListView issues={insight.teamIssues} canWrite={canWrite} />
          )}
          {tab === "context" && (
            <ContextTab
              docs={insight.teamDocs}
              projects={insight.teamProjects}
              onOpenDoc={onOpenDoc}
            />
          )}
          {tab === "activity" && (
            <ActivityTab runs={insight.scopedAgentRuns} users={users} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function OverviewTab({
  attention,
  activeProjects,
  contextDocs,
  agentRuns,
  issues,
  users,
  onOpenTask,
  onOpenProject,
  onOpenDoc,
  onNavigateTasks,
}: {
  attention: TeamAttentionItem[];
  activeProjects: Project[];
  contextDocs: Doc[];
  agentRuns: AgentRun[];
  issues: Issue[];
  users: User[];
  onOpenTask: (taskId: string) => void;
  onOpenProject: (projectId: string) => void;
  onOpenDoc: (docId: string) => void;
  onNavigateTasks: () => void;
}) {
  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="mx-auto grid w-full max-w-[1240px] gap-4 px-4 py-4 md:px-6 lg:grid-cols-2">
        <TeamModule
          title="Needs attention"
          icon={<AlertTriangle size={13} className="text-[var(--orange)]" />}
          count={attention.length}
          actionLabel="View tasks"
          onAction={onNavigateTasks}
        >
          {attention.length > 0 ? (
            attention
              .slice(0, 5)
              .map((item) => (
                <AttentionRow
                  key={item.issue.id}
                  item={item}
                  user={users.find(
                    (candidate) =>
                      candidate.id ===
                      humanOwnerId(item.issue)
                  )}
                  onOpen={() => onOpenTask(item.issue.id)}
                />
              ))
          ) : (
            <ModuleEmpty
              icon={<CheckCircle2 size={16} />}
              text="No overdue, urgent, or review work."
            />
          )}
        </TeamModule>

        <TeamModule
          title="Active projects"
          icon={<FolderKanban size={13} className="text-[var(--blue)]" />}
          count={activeProjects.length}
        >
          {activeProjects.length > 0 ? (
            activeProjects
              .slice(0, 5)
              .map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  tasks={issues.filter(
                    (issue) => issue.projectId === project.id
                  )}
                  onOpen={() => onOpenProject(project.id)}
                />
              ))
          ) : (
            <ModuleEmpty
              icon={<FolderKanban size={16} />}
              text="No active projects for this team."
            />
          )}
        </TeamModule>

        <TeamModule
          title="Pinned and recent docs"
          icon={<FileText size={13} className="text-[var(--purple)]" />}
          count={contextDocs.length}
        >
          {contextDocs.length > 0 ? (
            contextDocs.map((doc) => (
              <DocRow
                key={doc.id}
                doc={doc}
                onOpen={() => onOpenDoc(doc.id)}
              />
            ))
          ) : (
            <ModuleEmpty
              icon={<FileText size={16} />}
              text="No team context has been written yet."
            />
          )}
        </TeamModule>

        <TeamModule
          title="Current agent activity"
          icon={<Bot size={13} className="text-[var(--accent)]" />}
          count={agentRuns.length}
        >
          {agentRuns.length > 0 ? (
            agentRuns.map((run) => (
              <RunRow
                key={run.id}
                run={run}
                actor={users.find((user) => user.id === run.actorId)}
              />
            ))
          ) : (
            <ModuleEmpty
              icon={<Bot size={16} />}
              text="No agent has recorded work for this team."
            />
          )}
        </TeamModule>
      </div>
    </div>
  );
}

function ContextTab({
  docs,
  projects,
  onOpenDoc,
}: {
  docs: Doc[];
  projects: Project[];
  onOpenDoc: (docId: string) => void;
}) {
  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="mx-auto w-full max-w-[980px] px-4 py-5 md:px-6">
        <div className="mb-4">
          <h2 className="text-[15px] font-medium text-[var(--fg)]">
            Team context
          </h2>
          <p className="mt-0.5 text-[11.5px] text-[var(--fg-tertiary)]">
            Pinned team docs first, followed by recently updated project context.
          </p>
        </div>

        {docs.length > 0 ? (
          <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
            {docs.map((doc) => (
              <ContextDocRow
                key={doc.id}
                doc={doc}
                project={projects.find(
                  (candidate) => candidate.id === doc.projectId
                )}
                onOpen={() => onOpenDoc(doc.id)}
              />
            ))}
          </div>
        ) : (
          <LargeEmpty
            icon={<FileText size={19} />}
            title="No team context yet"
            body="Docs linked to this team or one of its projects will appear here."
          />
        )}
      </div>
    </div>
  );
}

function ActivityTab({
  runs,
  users,
}: {
  runs: AgentRun[];
  users: User[];
}) {
  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="mx-auto w-full max-w-[980px] px-4 py-5 md:px-6">
        <div className="mb-4">
          <h2 className="text-[15px] font-medium text-[var(--fg)]">
            Agent activity
          </h2>
          <p className="mt-0.5 text-[11.5px] text-[var(--fg-tertiary)]">
            Runs connected to the team through projects, tasks, and docs.
          </p>
        </div>

        {runs.length > 0 ? (
          <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
            {runs.map((run) => (
              <ActivityRunRow
                key={run.id}
                run={run}
                actor={users.find((user) => user.id === run.actorId)}
              />
            ))}
          </div>
        ) : (
          <LargeEmpty
            icon={<Bot size={19} />}
            title="No agent activity yet"
            body="Agent runs will appear after they record work against a team task, project, or doc."
          />
        )}
      </div>
    </div>
  );
}

function TeamModule({
  title,
  icon,
  count,
  actionLabel,
  onAction,
  children,
}: {
  title: string;
  icon: ReactNode;
  count: number;
  actionLabel?: string;
  onAction?: () => void;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)]">
      <div className="flex min-h-10 items-center gap-2 border-b border-[var(--border)] px-3">
        {icon}
        <h2 className="text-[12px] font-medium text-[var(--fg)]">{title}</h2>
        <span className="text-[10.5px] text-[var(--fg-muted)]">{count}</span>
        {actionLabel && onAction && (
          <button
            type="button"
            onClick={onAction}
            className="focus-ring ml-auto inline-flex items-center gap-1 rounded px-1.5 py-1 text-[10.5px] font-medium text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
          >
            {actionLabel}
            <ArrowRight size={10} />
          </button>
        )}
      </div>
      <div className="divide-y divide-[var(--border)]">{children}</div>
    </section>
  );
}

function AttentionRow({
  item,
  user,
  onOpen,
}: {
  item: TeamAttentionItem;
  user?: User;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="focus-ring group flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-[var(--bg-hover)]"
    >
      <PriorityIcon priority={item.issue.priority} size={13} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 font-mono text-[10px] text-[var(--fg-muted)]">
            {item.issue.identifier}
          </span>
          <span className="truncate text-[12px] font-medium text-[var(--fg)]">
            {item.issue.title}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {item.reasons.map((reason) => (
            <AttentionChip key={reason} reason={reason} />
          ))}
        </div>
      </div>
      <Avatar user={user} size={20} />
      <ArrowRight
        size={11}
        className="shrink-0 text-[var(--fg-muted)] transition-colors group-hover:text-[var(--fg-secondary)]"
      />
    </button>
  );
}

function AttentionChip({
  reason,
}: {
  reason: TeamAttentionItem["reasons"][number];
}) {
  const label =
    reason === "overdue"
      ? "Overdue"
      : reason === "review"
        ? "Review"
        : "Urgent";
  return (
    <span
      className={cn(
        "rounded border px-1.5 py-0.5 text-[9.5px] font-medium",
        reason === "overdue" || reason === "urgent"
          ? "border-[var(--red)]/30 bg-[var(--red)]/10 text-[var(--red)]"
          : "border-[var(--purple)]/30 bg-[var(--purple)]/10 text-[var(--purple)]"
      )}
    >
      {label}
    </span>
  );
}

function ProjectRow({
  project,
  tasks,
  onOpen,
}: {
  project: Project;
  tasks: Issue[];
  onOpen: () => void;
}) {
  const completed = tasks.filter((task) => task.status === "done").length;
  const committed = tasks.filter(
    (task) => task.status !== "backlog" && task.status !== "canceled"
  ).length;
  const progress = committed ? Math.round((completed / committed) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="focus-ring group flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[var(--bg-hover)]"
    >
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded border text-[13px]"
        style={{
          borderColor: `color-mix(in srgb, ${project.color} 32%, var(--border))`,
          background: `color-mix(in srgb, ${project.color} 8%, var(--bg-secondary))`,
        }}
      >
        {project.icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[12px] font-medium text-[var(--fg)]">
            {project.name}
          </span>
          <span className="shrink-0 capitalize text-[9.5px] text-[var(--fg-muted)]">
            {project.status.replace("_", " ")}
          </span>
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <span
            role="progressbar"
            aria-label={`${project.name} completion`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            className="h-1 flex-1 overflow-hidden rounded-full bg-[var(--bg-tertiary)]"
          >
            <span
              className="block h-full rounded-full bg-[var(--green)]"
              style={{ width: `${progress}%` }}
            />
          </span>
          <span className="shrink-0 text-[9.5px] text-[var(--fg-muted)]">
            {completed}/{committed}
          </span>
        </div>
      </div>
      {project.targetDate && (
        <span className="hidden shrink-0 items-center gap-1 text-[10px] text-[var(--fg-muted)] sm:flex">
          <Calendar size={10} />
          {formatDate(project.targetDate)}
        </span>
      )}
      <ArrowRight
        size={11}
        className="shrink-0 text-[var(--fg-muted)] transition-colors group-hover:text-[var(--fg-secondary)]"
      />
    </button>
  );
}

function DocRow({ doc, onOpen }: { doc: Doc; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="focus-ring group flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-[var(--bg-hover)]"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-[var(--border)] bg-[var(--bg-tertiary)] text-[13px]">
        {doc.icon || "📄"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {doc.pinned && (
            <Pin size={10} className="shrink-0 text-[var(--accent)]" />
          )}
          <span className="truncate text-[12px] font-medium text-[var(--fg)]">
            {docIdentifier(doc) ? `${docIdentifier(doc)} · ` : ""}{doc.title}
          </span>
        </div>
        <span className="mt-0.5 block text-[10px] text-[var(--fg-muted)]">
          {doc.pinned ? "Pinned context" : `Updated ${formatRelative(doc.updatedAt)}`}
        </span>
      </div>
      <ArrowRight
        size={11}
        className="shrink-0 text-[var(--fg-muted)] transition-colors group-hover:text-[var(--fg-secondary)]"
      />
    </button>
  );
}

function RunRow({
  run,
  actor,
}: {
  run: AgentRun;
  actor?: User;
}) {
  return (
    <div className="flex items-start gap-2 px-3 py-2.5">
      <Avatar user={actor} size={22} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[11.5px] font-medium text-[var(--fg)]">
            {actor?.name || "Agent"}
          </span>
          <RunStatus status={run.status} />
          <span className="ml-auto shrink-0 text-[9.5px] text-[var(--fg-muted)]">
            {formatRelative(run.endedAt ?? run.startedAt ?? run.createdAt)}
          </span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-[10.5px] leading-4 text-[var(--fg-tertiary)]">
          {run.summary || run.prompt || "Agent run recorded without a summary."}
        </p>
      </div>
    </div>
  );
}

function ContextDocRow({
  doc,
  project,
  onOpen,
}: {
  doc: Doc;
  project?: Project;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="focus-ring group flex w-full items-center gap-3 border-b border-[var(--border)] px-3 py-3 text-left transition-colors last:border-b-0 hover:bg-[var(--bg-hover)]"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] text-[15px]">
        {doc.icon || "📄"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {doc.pinned && (
            <Pin size={10} className="shrink-0 text-[var(--accent)]" />
          )}
          <span className="truncate text-[12.5px] font-medium text-[var(--fg)]">
            {docIdentifier(doc) ? `${docIdentifier(doc)} · ` : ""}{doc.title}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[var(--fg-muted)]">
          <span>{doc.pinned ? "Pinned" : `Updated ${formatRelative(doc.updatedAt)}`}</span>
          {project && (
            <span className="inline-flex items-center gap-1">
              <span>{project.icon}</span>
              {project.name}
            </span>
          )}
          {doc.tags.slice(0, 2).map((tag) => (
            <span key={tag}>#{tag}</span>
          ))}
        </div>
      </div>
      <ArrowRight
        size={11}
        className="shrink-0 text-[var(--fg-muted)] transition-colors group-hover:text-[var(--fg-secondary)]"
      />
    </button>
  );
}

function ActivityRunRow({
  run,
  actor,
}: {
  run: AgentRun;
  actor?: User;
}) {
  return (
    <div className="flex gap-3 border-b border-[var(--border)] px-3 py-3 last:border-b-0">
      <Avatar user={actor} size={26} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[12px] font-medium text-[var(--fg)]">
            {actor?.name || "Unknown agent"}
          </span>
          <RunStatus status={run.status} />
          <span className="text-[10px] text-[var(--fg-muted)]">
            {formatRelative(run.endedAt ?? run.startedAt ?? run.createdAt)}
          </span>
        </div>
        <p className="mt-1 text-[12px] leading-5 text-[var(--fg-secondary)]">
          {run.summary || run.prompt || "Agent run recorded without a summary."}
        </p>
        {run.actions.length > 0 && (
          <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[var(--fg-muted)]">
            <CheckCircle2 size={10} />
            {run.actions.length}{" "}
            {run.actions.length === 1 ? "action" : "actions"} recorded
          </div>
        )}
      </div>
    </div>
  );
}

function RunStatus({ status }: { status: AgentRun["status"] }) {
  const icon =
    status === "in_progress" ? (
      <Clock3 size={9} />
    ) : status === "completed" ? (
      <CheckCircle2 size={9} />
    ) : (
      <AlertTriangle size={9} />
    );
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded border px-1 py-0.5 text-[9px] font-medium capitalize",
        status === "in_progress"
          ? "border-[var(--blue)]/30 bg-[var(--blue)]/10 text-[var(--blue)]"
          : status === "completed"
            ? "border-[var(--green)]/30 bg-[var(--green)]/10 text-[var(--green)]"
            : "border-[var(--red)]/30 bg-[var(--red)]/10 text-[var(--red)]"
      )}
    >
      {icon}
      {status.replace("_", " ")}
    </span>
  );
}

function ModuleEmpty({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex min-h-[92px] items-center justify-center gap-2 px-4 py-6 text-center text-[11px] text-[var(--fg-muted)]">
      <span className="text-[var(--fg-muted)]">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

function LargeEmpty({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex min-h-[320px] items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-6 text-center">
      <div className="max-w-[360px]">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--fg-muted)]">
          {icon}
        </div>
        <h3 className="mt-3 text-[13.5px] font-medium text-[var(--fg)]">
          {title}
        </h3>
        <p className="mt-1 text-[11.5px] leading-5 text-[var(--fg-tertiary)]">
          {body}
        </p>
      </div>
    </div>
  );
}
